import {
    Button,
    SegmentedControl,
    SelectField,
    Text
} from '@7shifts/sous-chef-base';
import type { SelectOption } from '@7shifts/sous-chef-base';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@src/auth/AuthContext';
import { logout } from '@src/auth/logout';
import { useTerminalAccount } from '@src/context/AccountContext';
import { useTheme } from '@src/context/ThemeContext';
import { useTranslation } from '@src/i18n';
import { env } from '@src/config/env';
import { resetClient } from '@src/api/TimePunchClockApi';
import { VerifyPinModal } from './VerifyPinModal';
import './SettingsPage.css';

type PendingAction =
    | { type: 'logout' }
    | { type: 'changeLocation'; option: SelectOption<number> };

export function SettingsPage() {
    const { accessToken, refreshToken, mode, clearAuth } = useAuth();
    const { account, availableLocations, selectLocation, clearAccount, verifyPunchId } =
        useTerminalAccount();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { theme, source, setTheme } = useTheme();

    // Destructive actions (logout, change location) are gated behind entry of
    // the authenticated admin's punch ID (API-4551). The pad is re-presented on
    // every invocation — mounting this modal fresh each time resets its state.
    const [pendingAction, setPendingAction] = useState<PendingAction | null>(
        null
    );

    async function performLogout() {
        // logout() is designed to never throw, but a defensive try/catch
        // ensures teardown/redirect always proceeds (API-4797).
        if (mode === 'browser') {
            // Browser mode does a full-page redirect to FusionAuth's
            // /oauth2/logout (inside logout()) and lands back on /login, which
            // resets all React state anyway. We deliberately skip local
            // teardown here: calling clearAuth() would flip isAuthenticated to
            // false while we're still on /settings, causing RequireAuth to
            // stash /settings as the post-login redirect target — so after
            // signing back in the user would be sent to /settings instead of
            // the terminal (API-4797).
            try {
                await logout({ mode, accessToken, refreshToken });
            } catch {
                // Never let a revoke/redirect failure block logout.
            }
            return;
        }

        // Webview mode: no shared browser SSO cookie to clear via redirect.
        // Revoke the token, then tear down local state and navigate to /login.
        // navigate('/login') is batched with clearAuth() so RequireAuth never
        // re-renders /settings as unauthenticated (no stray redirect stash).
        try {
            await logout({ mode, accessToken, refreshToken });
        } catch {
            // Never let a revoke/redirect failure block local teardown.
        }

        // Release the Koin graph on logout (API-4681) so a subsequent
        // login always starts from a clean client.
        resetClient();
        clearAuth();
        clearAccount();
        void navigate('/login');
    }

    function performLocationChange(option: SelectOption<number>) {
        // Now that KMP tears down its Koin graph on rebuild (API-4681), the
        // switch happens in place — auth survives — so this is the same for
        // every mode (webview, iframe host, standalone browser).
        selectLocation(option.value);
        void navigate('/terminal');
    }

    function handleLogout() {
        setPendingAction({ type: 'logout' });
    }

    function handleLocationChange(option: SelectOption<number>) {
        if (option.value === account?.locationId) return;
        setPendingAction({ type: 'changeLocation', option });
    }

    function handleVerified() {
        const action = pendingAction;
        setPendingAction(null);
        if (!action) return;
        if (action.type === 'logout') {
            void performLogout();
        } else {
            performLocationChange(action.option);
        }
    }

    function handleThemeChange(selected: string) {
        setTheme(
            selected === t('settings.themeDark') ? 'dark' : 'light',
            'user'
        );
    }

    const locationOptions: SelectOption<number>[] = availableLocations.map(
        loc => ({ value: loc.id, label: loc.name })
    );
    const selectedLocationOption = locationOptions.find(
        o => o.value === account?.locationId
    );
    const showLocationPicker = availableLocations.length > 1;
    const themeOptions = [t('settings.themeLight'), t('settings.themeDark')];
    const themeValue =
        theme === 'dark' ? t('settings.themeDark') : t('settings.themeLight');
    const isUrlLocked = source === 'url';

    return (
        <main className="settings-page">
            <div className="settings-page__container">
                <Text as="h1" alignment="center">
                    {t('common.settings')}
                </Text>

                {showLocationPicker && (
                    <SelectField
                        name="location"
                        label={t('settings.changeLocation')}
                        options={locationOptions}
                        value={selectedLocationOption}
                        onChange={handleLocationChange}
                        isSearchable={false}
                    />
                )}

                <div className="settings-page__theme">
                    <Text emphasis="bold">{t('settings.theme')}</Text>
                    <div className="settings-page__theme-control">
                        <SegmentedControl
                            options={themeOptions}
                            value={themeValue}
                            onChange={handleThemeChange}
                            testId="settings-theme-toggle"
                        />
                    </div>
                    {isUrlLocked && (
                        <span className="settings-page__theme-locked-note">
                            {t('settings.themeUrlLocked')}
                        </span>
                    )}
                </div>

                <Button
                    type="button"
                    theme="danger"
                    size="full-width"
                    onClick={handleLogout}
                    testId="settings-logout"
                >
                    {t('settings.logout')}
                </Button>

                <span className="settings-page__version">{env.appVersion}</span>
            </div>

            {pendingAction && (
                <VerifyPinModal
                    verify={verifyPunchId}
                    title={
                        pendingAction.type === 'logout'
                            ? t('settings.verifyLogoutTitle')
                            : t('settings.verifyChangeLocationTitle')
                    }
                    submitLabel={
                        pendingAction.type === 'logout'
                            ? t('settings.verifyLogoutSubmit')
                            : t('settings.verifyChangeLocationSubmit')
                    }
                    onVerified={handleVerified}
                    onClose={() => setPendingAction(null)}
                />
            )}
        </main>
    );
}
